import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Components
import LandingPage from './components/LandingPage';
import Login from './components/Login';
import Register from './components/Register';
import PendingApproval from './components/PendingApproval';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import Dashboard from './components/Dashboard';
import Profile from './components/Profile';
import Settings from './components/Settings';
import Contact from './components/Contact';
import ExternalContact from './components/ExternalContact';
import Admin from './components/Admin';
import PatientManagement from './components/admin/PatientManagement';
import PhysicianManagement from './components/admin/PhysicianManagement';
import SponsorManagement from './components/admin/SponsorManagement';
import Loading from './components/Loading';
import ComingSoon from './components/ComingSoon';
import VideoCall from './components/VideoCall';
import Appointments from './components/Appointments';
import Messages from './components/Messages';
import Prescriptions from './components/Prescriptions';
import PhysicianLogin from './components/PhysicianLogin';
import ClinicDashboard from './components/ClinicDashboard';
import ClinicStaffDashboard from './components/ClinicStaffDashboard';
import SponsorDashboard from './components/SponsorDashboard';
import Notes from './components/Notes';
import ClinicPatientManagement from './components/clinic/PatientManagement';
import PhysicianDirectory from './components/patient/PhysicianDirectory';
import Documentation from './components/Documentation';
import ScheduleManagement from './components/physician/ScheduleManagement';
import NotFound from './components/NotFound';

// Layout and Navigation
import AppLayout from './components/common/AppLayout';
import ErrorBoundary from './components/common/ErrorBoundary';
import { patientNavItems, physicianNavItems, sponsorNavItems, adminNavItems } from './navigation/navItems.jsx';

// Protected Route Component using new Auth Context
// Checks authentication and optionally approval status for physicians/sponsors
const ProtectedRoute = ({ children, requireApproval = false }) => {
  const { loading, isAuthenticated, isApproved } = useAuth();

  if (loading) {
    return <Loading message="Authenticating..." />;
  }

  if (!isAuthenticated) {
    // Immediately redirect to login when not authenticated
    return <Navigate to="/login" replace />;
  }

  // Check approval for routes that require it (physician/sponsor routes)
  if (requireApproval && !isApproved) {
    return <Navigate to="/pending-approval" replace />;
  }

  return children;
};

// App Routes Component
function AppRoutes() {
  const { profile, isAdmin, isProvider, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();

  // Handle navigation when user signs out
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      // User is not authenticated - ensure we're on a public route
      const publicRoutes = ['/', '/login', '/register', '/pending-approval', '/forgot-password', '/reset-password', '/external-contact', '/physician-login'];
      const currentPath = window.location.pathname;

      if (!publicRoutes.includes(currentPath)) {
        // User signed out but still on a protected route - redirect to login
        navigate('/login', { replace: true });
      }
    }
  }, [isAuthenticated, loading, navigate]);

  // Determine which nav items to use based on user role and current path
  const getNavItems = () => {
    const currentPath = window.location.pathname;

    // If on admin routes, show admin nav
    if (currentPath.startsWith('/admin') && isAdmin) {
      return adminNavItems;
    }

    // If on physician routes, show physician nav
    if ((currentPath.startsWith('/physician') || currentPath.startsWith('/clinic-dashboard')) && isProvider) {
      return physicianNavItems;
    }

    // Default fallback based on role priority
    if (isAdmin) {
      return adminNavItems;
    }

    if (isProvider) {
      return physicianNavItems;
    }

    const userRole = profile?.tenant_role;
    if (userRole === 'sponsor') {
      return sponsorNavItems;
    }

    // Default to patient nav items
    return patientNavItems;
  };

  return (
    <Routes>
      {/* Public routes without navigation */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/pending-approval" element={<PendingApproval />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/external-contact" element={<ExternalContact />} />
      <Route path="/physician-login" element={<PhysicianLogin />} />

      {/* Protected routes with navigation layout */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <AppLayout navItems={getNavItems()}>
            <Dashboard />
          </AppLayout>
        </ProtectedRoute>
      } />

      <Route path="/appointments" element={
        <ProtectedRoute>
          <AppLayout navItems={getNavItems()}>
            <Appointments />
          </AppLayout>
        </ProtectedRoute>
      } />

      <Route path="/physicians" element={
        <ProtectedRoute>
          <AppLayout navItems={getNavItems()}>
            <PhysicianDirectory />
          </AppLayout>
        </ProtectedRoute>
      } />

      <Route path="/messages" element={
        <ProtectedRoute>
          <AppLayout navItems={getNavItems()}>
            <Messages />
          </AppLayout>
        </ProtectedRoute>
      } />

      <Route path="/video-call" element={
        <ProtectedRoute>
          <AppLayout navItems={getNavItems()}>
            <VideoCall />
          </AppLayout>
        </ProtectedRoute>
      } />

      <Route path="/prescriptions" element={
        <ProtectedRoute>
          <AppLayout navItems={getNavItems()}>
            <Prescriptions />
          </AppLayout>
        </ProtectedRoute>
      } />

      <Route path="/profile" element={
        <ProtectedRoute>
          <AppLayout navItems={getNavItems()}>
            <Profile />
          </AppLayout>
        </ProtectedRoute>
      } />

      <Route path="/settings" element={
        <ProtectedRoute>
          <AppLayout navItems={getNavItems()}>
            <Settings />
          </AppLayout>
        </ProtectedRoute>
      } />

      <Route path="/documentation" element={
        <ProtectedRoute>
          <Documentation />
        </ProtectedRoute>
      } />

      <Route path="/contact" element={
        <ProtectedRoute>
          <AppLayout navItems={getNavItems()}>
            <Contact />
          </AppLayout>
        </ProtectedRoute>
      } />

      <Route path="/notes" element={
        <ProtectedRoute>
          <AppLayout navItems={getNavItems()}>
            <Notes />
          </AppLayout>
        </ProtectedRoute>
      } />

      {/* Patient routes - coming soon */}
      <Route path="/health-stats" element={
        <ProtectedRoute>
          <AppLayout navItems={getNavItems()}>
            <ComingSoon title="Health Stats" />
          </AppLayout>
        </ProtectedRoute>
      } />

      <Route path="/records" element={
        <ProtectedRoute>
          <AppLayout navItems={getNavItems()}>
            <ComingSoon title="Health Records" />
          </AppLayout>
        </ProtectedRoute>
      } />

      <Route path="/healthcare-services" element={
        <ProtectedRoute>
          <AppLayout navItems={getNavItems()}>
            <ComingSoon title="Healthcare Services" />
          </AppLayout>
        </ProtectedRoute>
      } />

      <Route path="/lifestyle" element={
        <ProtectedRoute>
          <AppLayout navItems={getNavItems()}>
            <ComingSoon title="Lifestyle & Support" />
          </AppLayout>
        </ProtectedRoute>
      } />

      <Route path="/admin" element={
        <ProtectedRoute>
          <AppLayout navItems={getNavItems()}>
            <Admin />
          </AppLayout>
        </ProtectedRoute>
      } />

      <Route path="/admin/security" element={
        <ProtectedRoute>
          <AppLayout navItems={getNavItems()}>
            <ComingSoon title="Security Management" />
          </AppLayout>
        </ProtectedRoute>
      } />

      <Route path="/admin/admins" element={
        <ProtectedRoute>
          <AppLayout navItems={getNavItems()}>
            <ComingSoon title="Admin User Management" />
          </AppLayout>
        </ProtectedRoute>
      } />

      <Route path="/clinic-dashboard" element={
        <ProtectedRoute requireApproval={true}>
          <AppLayout navItems={getNavItems()}>
            <ClinicDashboard />
          </AppLayout>
        </ProtectedRoute>
      } />

      <Route path="/clinic/patients" element={
        <ProtectedRoute>
          <AppLayout navItems={getNavItems()}>
            <ClinicPatientManagement />
          </AppLayout>
        </ProtectedRoute>
      } />

      <Route path="/clinic-staff-dashboard" element={
        <ProtectedRoute>
          <AppLayout navItems={getNavItems()}>
            <ClinicStaffDashboard />
          </AppLayout>
        </ProtectedRoute>
      } />

      <Route path="/physician/schedule" element={
        <ProtectedRoute requireApproval={true}>
          <AppLayout navItems={getNavItems()}>
            <ScheduleManagement />
          </AppLayout>
        </ProtectedRoute>
      } />

      {/* Physician-specific routes */}
      <Route path="/physician/patients" element={
        <ProtectedRoute requireApproval={true}>
          <AppLayout navItems={getNavItems()}>
            <ComingSoon title="Patient Management" />
          </AppLayout>
        </ProtectedRoute>
      } />

      <Route path="/physician/records" element={
        <ProtectedRoute requireApproval={true}>
          <AppLayout navItems={getNavItems()}>
            <ComingSoon title="Medical Records" />
          </AppLayout>
        </ProtectedRoute>
      } />

      <Route path="/physician/profile" element={
        <ProtectedRoute requireApproval={true}>
          <AppLayout navItems={getNavItems()}>
            <Profile />
          </AppLayout>
        </ProtectedRoute>
      } />

      <Route path="/physician/settings" element={
        <ProtectedRoute requireApproval={true}>
          <AppLayout navItems={getNavItems()}>
            <Settings />
          </AppLayout>
        </ProtectedRoute>
      } />

      <Route path="/sponsor-dashboard" element={
        <ProtectedRoute requireApproval={true}>
          <AppLayout navItems={getNavItems()}>
            <SponsorDashboard />
          </AppLayout>
        </ProtectedRoute>
      } />

      {/* Sponsor-specific routes - require approval */}
      <Route path="/sponsor/family" element={
        <ProtectedRoute requireApproval={true}>
          <AppLayout navItems={getNavItems()}>
            <ComingSoon title="Family Members" />
          </AppLayout>
        </ProtectedRoute>
      } />

      <Route path="/sponsor/payments" element={
        <ProtectedRoute requireApproval={true}>
          <AppLayout navItems={getNavItems()}>
            <ComingSoon title="Payments" />
          </AppLayout>
        </ProtectedRoute>
      } />

      <Route path="/sponsor/profile" element={
        <ProtectedRoute requireApproval={true}>
          <AppLayout navItems={getNavItems()}>
            <Profile />
          </AppLayout>
        </ProtectedRoute>
      } />

      <Route path="/sponsor/settings" element={
        <ProtectedRoute requireApproval={true}>
          <AppLayout navItems={getNavItems()}>
            <Settings />
          </AppLayout>
        </ProtectedRoute>
      } />

      <Route path="/sponsor/contact" element={
        <ProtectedRoute requireApproval={true}>
          <AppLayout navItems={getNavItems()}>
            <Contact />
          </AppLayout>
        </ProtectedRoute>
      } />

      {/* Admin routes */}
      <Route path="/admin/physicians" element={
        <ProtectedRoute>
          <AppLayout navItems={getNavItems()}>
            <PhysicianManagement />
          </AppLayout>
        </ProtectedRoute>
      } />

      <Route path="/admin/patients" element={
        <ProtectedRoute>
          <AppLayout navItems={getNavItems()}>
            <PatientManagement />
          </AppLayout>
        </ProtectedRoute>
      } />

      <Route path="/admin/sponsors" element={
        <ProtectedRoute>
          <AppLayout navItems={getNavItems()}>
            <SponsorManagement />
          </AppLayout>
        </ProtectedRoute>
      } />

      <Route path="/admin/settings" element={
        <ProtectedRoute>
          <AppLayout navItems={getNavItems()}>
            <ComingSoon title="System Settings" />
          </AppLayout>
        </ProtectedRoute>
      } />

      <Route path="*" element={
        <ProtectedRoute>
          <AppLayout navItems={getNavItems()}>
            <NotFound />
          </AppLayout>
        </ProtectedRoute>
      } />
    </Routes>
  );
}

import { ViewProvider } from './contexts/ViewContext';

// Main App Component wrapped with AuthProvider
function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <ViewProvider>
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#363636',
                color: '#fff',
              },
              success: {
                duration: 3000,
                iconTheme: {
                  primary: '#25CEC8',
                  secondary: '#fff',
                },
              },
              error: {
                duration: 4000,
                iconTheme: {
                  primary: '#FF5A5A',
                  secondary: '#fff',
                },
              },
            }}
          />
          <AppRoutes />
        </ViewProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
